/* eslint-disable camelcase */
/**
 * Reading the caller's identity out of the access token (SMN-67).
 *
 * This **decodes**; it does not verify. The browser has no signing key and never
 * will. Verification is the server's job and already happens on every request
 * (SMN-36/37): a token this platform did not sign, or one naming a role it does
 * not recognise, is refused there.
 *
 * That is exactly why decoding is enough here. Identity used to come from an
 * `id` cookie the page itself had written, so anything running in the browser
 * could change it and the portal would believe it -- and then fetch and display
 * whichever account it named. A claim inside a signed token cannot be edited
 * into something the API will honour: alter it and every request 401s.
 *
 * The guards built on this are still only UX. The boundary is the server. But
 * they are now UX derived from what the server actually issued, rather than
 * from a value the client wrote to itself.
 */

export interface AccessTokenClaims {
  id: string
  user_email: string
  user_account_type: string
  user_username?: string
  is_locked?: boolean
  /** Expiry, seconds since the epoch. Finite since SMN-47 (finding 3.5). */
  exp?: number
}

/** One base64url segment of a JWT, as text. */
function decodeSegment(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)

  // atob yields one character per byte. The payload is UTF-8, so a name or an
  // address outside ASCII would come back mangled without this step.
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/**
 * The claims carried by `token`, or null if it does not carry any.
 *
 * Structural only -- it says nothing about whether the token is still good.
 * Use `identityFromToken` for that; this is exported for its own tests and for
 * callers that genuinely want the payload of an expired token.
 */
export function decodeAccessToken(token: unknown): AccessTokenClaims | null {
  if (typeof token !== 'string') return null

  const segments = token.split('.')
  if (segments.length !== 3) return null

  try {
    const claims = JSON.parse(decodeSegment(segments[1]))
    if (claims === null || typeof claims !== 'object') return null
    return claims as AccessTokenClaims
  } catch (error) {
    // Anything at all: not base64, not JSON, truncated by a cookie limit. A
    // token we cannot read is a caller we cannot identify, which is the same
    // answer as having no token.
    return null
  }
}

/**
 * Who the token says the caller is, or null if it cannot say.
 *
 * Null covers every case in which the portal must behave as though nobody is
 * signed in: no token, an unreadable one, one missing the claims identity is
 * built from, an expired one, or one belonging to a locked account.
 *
 * Expiry is checked here rather than left to the first failing request. Before
 * SMN-47 tokens never expired, so "has a token" and "is signed in" were the
 * same thing; they are not any more, and a portal that thinks it is signed in
 * with a dead token shows a full interface that errors on every click.
 */
export function identityFromToken(
  token: unknown,
  now: number = Date.now()
): AccessTokenClaims | null {
  const claims = decodeAccessToken(token)
  if (!claims) return null

  if (!claims.id || !claims.user_account_type) return null

  // Findings 3.23/3.27: a lock has to hold everywhere, not only at sign-in.
  if (claims.is_locked === true) return null

  if (typeof claims.exp === 'number' && claims.exp * 1000 <= now) return null

  return claims
}
